import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

const RecordContext = createContext(null);

export const MAX_SELECTED_RECORDS = 50000
export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE = 1;

// 元件外的 state cache，讓 unmount 後再 mount 時能還原 page 和 filters
const tableStateCache = new Map();

export function RecordProvider({ tableId, supportRules, children, lastModified, queries, defaultQuery, service, onQueryChange }) {
  const queryClient = useQueryClient();

  const initialQuery = { ...queries } // 合併傳入的 queries
  supportRules && supportRules.forEach(rule => {
    if (rule.defaultValue !== undefined) {
      initialQuery[rule.inputName] = rule.defaultValue
    }
  })

  // 從 cache 還原 query 和 page（如果有的話）
  const cached = tableId ? tableStateCache.get(tableId) : null;
  const [query, setQuery] = useState(cached?.query || initialQuery);

  const [selected, setSelected] = useState(new Set());
  const [isAllRecords, setIsAllRecords] = useState(false);

  const [page, setPage] = useState(cached?.page || DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem(`table-page-size`)) || DEFAULT_PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);

  const getSortingPreference = (tableId) => {
    if (!tableId) return null;
    const key = `gowarehouse-table-sorting-${tableId}`;
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.warn('Failed to parse sorting preference from localStorage:', e);
      return null;
    }
  };

  const saveSortingPreference = (tableId, field, direction) => {
    if (!tableId) return;
    const key = `gowarehouse-table-sorting-${tableId}`;
    try {
      localStorage.setItem(key, JSON.stringify({ field, direction }));
    } catch (e) {
      console.warn('Failed to save sorting preference to localStorage:', e);
    }
  };

  const getInitialSorting = () => {
    const savedPreference = getSortingPreference(tableId);
    if (savedPreference) {
      return {
        field: savedPreference.field,
        direction: savedPreference.direction
      };
    }
    return {
      field: defaultQuery?.sorting_field || 'id',
      direction: defaultQuery?.sorting_direction || 'desc'
    };
  };

  const initialSorting = getInitialSorting();
  const [sortingField, setSortingField] = useState(initialSorting.field);
  const [sortingDirection, setSortingDirection] = useState(initialSorting.direction);

  // React Query: queryKey 包含所有會觸發 reset 的參數
  const queryKey = useMemo(() =>
    ['table-records', tableId, query, sortingField, sortingDirection, pageSize],
    [tableId, query, sortingField, sortingDirection, pageSize]
  );

  // 主要查詢 — 取得第一頁資料（或從快取返回）
  const {
    data: queryData,
    isFetching,
    isError: hasError,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const payload = {
        ...defaultQuery,
        ...query,
        page: 1,
        page_size: pageSize,
        sorting_field: sortingField,
        sorting_direction: sortingDirection
      };

      const data = await service.getRecords(payload);
      return { records: data.data, hasMore: data.has_more, loadedPage: 1 };
    },
    enabled: !!service && typeof service.getRecords === 'function',
    placeholderData: (prev) => prev, // 切換 queryKey 時保留舊資料避免閃爍
  });



  const records = queryData?.records || [];
  const hasMore = queryData?.hasMore || false;
  const loadedPage = queryData?.loadedPage || 1;

  // 初次查詢或 queryKey 變更時重置 page
  const prevQueryKeyRef = useRef(JSON.stringify(queryKey));
  useEffect(() => {
    const keyStr = JSON.stringify(queryKey);
    if (prevQueryKeyRef.current !== keyStr) {
      prevQueryKeyRef.current = keyStr;
      setPage(1);
    }
  }, [queryKey]);

  // lastModified 變更時（mutation 發生）→ 重新 fetch
  const prevLastModified = useRef(lastModified);
  useEffect(() => {
    if (prevLastModified.current !== lastModified) {
      prevLastModified.current = lastModified;
      setPage(1);
      queryClient.invalidateQueries({ queryKey });
    }
  }, [lastModified, queryClient, queryKey]);

  // 當 query 變化時通知父組件
  useEffect(() => {
    if (onQueryChange) {
      onQueryChange(query);
    }
  }, [query, onQueryChange]);

  // 同步 page 和 query 到 cache，供下次 mount 還原
  useEffect(() => {
    if (tableId) {
      tableStateCache.set(tableId, { page, query });
    }
  }, [tableId, page, query]);

  // 進入清單時（mount）invalidate 一次，確保不論 PUSH（點側欄連結）或 POP
  // （新增/編輯存檔後 goBack 返回）都會 refetch 拿到最新資料。
  // 靠 placeholderData 避免閃白、tableStateCache + backfill 保留分頁與捲動狀態。
  const hasInvalidatedRef = useRef(false);
  useEffect(() => {
    if (!hasInvalidatedRef.current) {
      hasInvalidatedRef.current = true;
      queryClient.invalidateQueries({ queryKey: ['table-records', tableId] });
    }
  }, [tableId, queryClient]);

  // 翻頁或背景 refetch 後載入更多資料（補齊當前 page 所需）
  useEffect(() => {
    if (page > 1 && page * pageSize > records.length && hasMore && page > loadedPage) {
      fetchMoreRecords(loadedPage + 1);
    }
  }, [page, loadedPage]);

  const fetchMoreRecords = async (nextPage) => {
    if (!service || typeof service.getRecords !== 'function') return;

    setLoadingMore(true);
    try {
      const payload = {
        ...defaultQuery,
        ...query,
        page: nextPage,
        page_size: pageSize,
        sorting_field: sortingField,
        sorting_direction: sortingDirection
      };

      const data = await service.getRecords(payload);

      // 更新 React Query 快取，附加新頁資料
      queryClient.setQueryData(queryKey, (old) => ({
        records: [...(old?.records || []), ...data.data],
        hasMore: data.has_more,
        loadedPage: nextPage,
      }));
    } catch (err) {
      console.error('[TableIndex] Failed to fetch more records:', err);
      message.error('載入資料失敗，請稍後再試');
    } finally {
      setLoadingMore(false);
    }
  };

  const loading = isFetching || loadingMore;

  // 保留 getRecords 介面供外部呼叫（如 batch 操作後刷新）
  const getRecords = useCallback(async (reset = false) => {
    if (reset) {
      setPage(1);
      return refetch().then(result => result.data?.records || []);
    }
  }, [refetch]);

  // 包裝 setQuery：使用者手動操作（搜尋、套用篩選）時一律 invalidate 快取
  const setQueryAndRefresh = useCallback((newQuery) => {
    setQuery(newQuery);
    setPage(1);
    queryClient.invalidateQueries({ queryKey: ['table-records', tableId] });
  }, [queryClient, tableId]);

  const onChangeSorting = (selectColumn, direction) => {
    setSortingField(selectColumn.sortFieldName);
    setSortingDirection(direction);
    saveSortingPreference(tableId, selectColumn.sortFieldName, direction);
  };

  // useCallback 鎖 ref：消費者（Table.tsx paginationComponent useCallback）把這兩個 fn 放進 dep。
  // 沒包的話 Provider 每 render（如 background job 進度通知洪流觸發的 context cascade）就新 ref →
  // 下游 useCallback 跟著失效 → RDT 把 pagination 子樹當不同 component remount → button 閃且點不下去。
  const onChangePage = useCallback((page) => {
    setPage(page)
  }, []);

  const onChangePageSize = useCallback((page_size) => {
    localStorage.setItem(`table-page-size`, page_size);
    setPageSize(page_size)
    setPage(1) // 改變每頁筆數時重設為第一頁，避免請求不存在的頁面
  }, []);

  const selectAllRecords = async () => {
    if (!service || typeof service.getIds !== 'function') {
      return [];
    }

    try {
      const payload = {
        ...query
      };

      const data = await service.getIds(payload);
      const ids = data.data || [];
      setSelected(new Set(ids));
      return ids;
    } catch (err) {
      console.error('[TableIndex] Failed to select all records:', err);
      message.error('選擇全部記錄失敗，請稍後再試');
      throw err; // Re-throw to propagate error
    }
  };

  const onSelectAllRecords = async (checked) => {
    if (checked) {
      setSelected(new Set());
      return [];
    } else {
      try {
        const ids = await selectAllRecords();
        return ids;
      } catch (err) {
        console.error('[TableIndex] Failed to select all records on check:', err);
        throw err;
      }
    }
  };

  const onSelectRecord = (id) => {
    // select or undo current page data
    selected.has(id) ? selected.delete(id) : selected.add(id);
    setSelected(new Set(selected));
  };

  const checkedAll = () => {
    const all = Array.isArray(records) && records.length > 0 && records.every(({ id }) => selected.has(id));
    setIsAllRecords(all);
  };

  const toggleCurrentAllRecords = () => {
    const iDs = isAllRecords ? [] : records.map(({ id }) => id);
    setSelected(new Set(iDs));
  };

  const clearSelected = () => setSelected(new Set());

  const clearAllSettings = () => {
    if (!tableId) return;

    // Clear sorting preference
    const sortingKey = `gowarehouse-table-sorting-${tableId}`;
    localStorage.removeItem(sortingKey);

    // Clear pinned rules
    const pinnedRulesKey = `pinnedRule-${tableId}`;
    localStorage.removeItem(pinnedRulesKey);

    // Clear page size
    localStorage.removeItem('table-page-size');

    // Reload page to apply changes
    message.success('已清除所有設置，頁面即將重新載入');
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const dependencies = {
    selected,
    clearSelected,

    getRecords,
    query,
    setQuery: setQueryAndRefresh,

    records,
    onSelectRecord,
    onSelectAllRecords,
    toggleCurrentAllRecords,

    isAllRecords,
    hasMore,
    onChangeSorting,
    onChangePage,
    pageSize,
    onChangePageSize,
    page,
    loading,
    hasError,
    clearAllSettings

  };

  const value = useMemo(() => dependencies, Object.values(dependencies));


  useEffect(() => {
    checkedAll();
  }, [selected]);


  return <RecordContext.Provider value={value}>{children}</RecordContext.Provider>;
}

export const useRecordContext = () => {
  return useContext(RecordContext);
};
